import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../utils/api';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const StatusTypeSettings = () => {
  const [statusTypes, setStatusTypes] = useState([]);
  const [entityTypes, setEntityTypes] = useState([
    { id: 'project', name: 'Projects', active: true },
    { id: 'phase', name: 'Phases', active: false },
    { id: 'step', name: 'Steps', active: false }
  ]);
  const [activeEntityType, setActiveEntityType] = useState('project');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [currentStatusType, setCurrentStatusType] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    color: '#6B7280',
    description: '',
    entityTypes: ['project'],
    is_completed: false,
    is_canceled: false
  });

  // Fetch status types for the active entity type
  useEffect(() => {
    fetchStatusTypes();
  }, [activeEntityType]);

  const fetchStatusTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/status-types/${activeEntityType}`);
      if (response.data && response.data.statusTypes) {
        setStatusTypes(response.data.statusTypes);
      }
    } catch (error) {
      console.error('Error fetching status types:', error);
      toast.error('Failed to load status types');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityTypeClick = (entityTypeId) => {
    setActiveEntityType(entityTypeId);
    setEntityTypes(entityTypes.map(et => ({
      ...et,
      active: et.id === entityTypeId
    })));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'entityTypes') {
      // Handle entity type checkboxes
      const updatedEntityTypes = type === 'checkbox' 
        ? checked 
          ? [...formData.entityTypes, value]
          : formData.entityTypes.filter(type => type !== value)
        : value;
      
      setFormData({
        ...formData,
        entityTypes: updatedEntityTypes
      });
    } else {
      // Handle other inputs
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      color: '#6B7280',
      description: '',
      entityTypes: ['project'],
      is_completed: false,
      is_canceled: false
    });
  };

  const handleAddClick = () => {
    resetForm();
    setShowAddForm(true);
    setShowEditForm(false);
  };

  const handleEditClick = (statusType) => {
    setCurrentStatusType(statusType);
    setFormData({
      name: statusType.name,
      color: statusType.color,
      description: statusType.description || '',
      entityTypes: ['project', 'phase', 'step'].filter(entityType => 
        statusType[`is_${entityType}_status`] || entityType === activeEntityType
      ),
      is_completed: statusType.is_completed || false,
      is_canceled: statusType.is_canceled || false
    });
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.post('/settings/status-types', formData);
      
      if (response.data && response.data.statusType) {
        toast.success('Status type created successfully');
        setShowAddForm(false);
        resetForm();
        fetchStatusTypes();
      }
    } catch (error) {
      console.error('Error creating status type:', error);
      toast.error(error.response?.data?.error || 'Failed to create status type');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.put(`/settings/status-types/${currentStatusType.id}`, formData);
      
      if (response.data && response.data.statusType) {
        toast.success('Status type updated successfully');
        setShowEditForm(false);
        setCurrentStatusType(null);
        resetForm();
        fetchStatusTypes();
      }
    } catch (error) {
      console.error('Error updating status type:', error);
      toast.error(error.response?.data?.error || 'Failed to update status type');
    }
  };

  const handleDeleteClick = async (statusType) => {
    if (statusType.is_default) {
      toast.error('Cannot delete default status types');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${statusType.name}"?`)) {
      return;
    }
    
    try {
      await api.delete(`/settings/status-types/${statusType.id}`);
      toast.success('Status type deleted successfully');
      fetchStatusTypes();
    } catch (error) {
      console.error('Error deleting status type:', error);
      toast.error(error.response?.data?.error || 'Failed to delete status type');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(statusTypes);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update the local state immediately for a responsive UI
    setStatusTypes(items);
    
    // Send the updated order to the server
    try {
      await api.post(`/settings/status-types/${activeEntityType}/order`, {
        order: items.map(item => item.id)
      });
    } catch (error) {
      console.error('Error updating status type order:', error);
      toast.error('Failed to update status order');
      // Revert to the original order if the API call fails
      fetchStatusTypes();
    }
  };

  const colorOptions = [
    { name: 'Gray', value: '#6B7280' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Green', value: '#10B981' },
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Status Types</h1>
        <p className="mt-2 text-gray-600">
          Manage the available status types for projects, phases, and steps.
        </p>
      </div>

      {/* Entity type selector */}
      <div className="mb-6">
        <div className="sm:hidden">
          <label htmlFor="entity-type-select" className="sr-only">
            Select Entity Type
          </label>
          <select
            id="entity-type-select"
            className="block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            value={activeEntityType}
            onChange={(e) => handleEntityTypeClick(e.target.value)}
          >
            {entityTypes.map((entityType) => (
              <option key={entityType.id} value={entityType.id}>
                {entityType.name}
              </option>
            ))}
          </select>
        </div>
        <div className="hidden sm:block">
          <nav className="flex space-x-4" aria-label="Tabs">
            {entityTypes.map((entityType) => (
              <button
                key={entityType.id}
                onClick={() => handleEntityTypeClick(entityType.id)}
                className={`${
                  entityType.active
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700'
                } px-3 py-2 font-medium text-sm rounded-md`}
                aria-current={entityType.active ? 'page' : undefined}
              >
                {entityType.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Add button and forms */}
      <div className="mb-6">
        {!showAddForm && !showEditForm && (
          <button
            type="button"
            onClick={handleAddClick}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Add New Status Type
          </button>
        )}

        {/* Add form */}
        {showAddForm && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Add New Status Type</h2>
            <form onSubmit={handleAddSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                    Color
                  </label>
                  <select
                    name="color"
                    id="color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    {colorOptions.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.name}
                      </option>
                    ))}
                  </select>
                  <div 
                    className="mt-2 h-6 w-full rounded"
                    style={{ backgroundColor: formData.color }}
                  ></div>
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Apply to
                </span>
                <div className="flex items-center space-x-6">
                  {entityTypes.map((entityType) => (
                    <div key={entityType.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`entity-${entityType.id}`}
                        name="entityTypes"
                        value={entityType.id}
                        checked={formData.entityTypes.includes(entityType.id)}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`entity-${entityType.id}`}
                        className="ml-2 block text-sm text-gray-700"
                      >
                        {entityType.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-4 border-t border-gray-200 pt-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Status Type Flags
                </span>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_completed"
                      name="is_completed"
                      checked={formData.is_completed}
                      onChange={(e) => setFormData({...formData, is_completed: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="is_completed"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Mark as "Completed" status
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_canceled"
                      name="is_canceled"
                      checked={formData.is_canceled}
                      onChange={(e) => setFormData({...formData, is_canceled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="is_canceled"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Mark as "Canceled" status
                    </label>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  These settings determine whether projects with this status are considered completed or canceled.
                  Projects with these statuses will be filtered from the default dashboard view.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Edit form */}
        {showEditForm && currentStatusType && (
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Edit Status Type: {currentStatusType.name}
            </h2>
            <form onSubmit={handleEditSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="edit-name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="edit-color" className="block text-sm font-medium text-gray-700">
                    Color
                  </label>
                  <select
                    name="color"
                    id="edit-color"
                    value={formData.color}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    {colorOptions.map((color) => (
                      <option key={color.value} value={color.value}>
                        {color.name}
                      </option>
                    ))}
                  </select>
                  <div 
                    className="mt-2 h-6 w-full rounded"
                    style={{ backgroundColor: formData.color }}
                  ></div>
                </div>
              </div>
              
              <div className="mb-4">
                <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  name="description"
                  id="edit-description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                ></textarea>
              </div>
              
              <div className="mb-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Apply to
                </span>
                <div className="flex items-center space-x-6">
                  {entityTypes.map((entityType) => (
                    <div key={entityType.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`edit-entity-${entityType.id}`}
                        name="entityTypes"
                        value={entityType.id}
                        checked={formData.entityTypes.includes(entityType.id)}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor={`edit-entity-${entityType.id}`}
                        className="ml-2 block text-sm text-gray-700"
                      >
                        {entityType.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-4 border-t border-gray-200 pt-4">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Status Type Flags
                </span>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_is_completed"
                      name="is_completed"
                      checked={formData.is_completed}
                      onChange={(e) => setFormData({...formData, is_completed: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="edit_is_completed"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Mark as "Completed" status
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="edit_is_canceled"
                      name="is_canceled"
                      checked={formData.is_canceled}
                      onChange={(e) => setFormData({...formData, is_canceled: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="edit_is_canceled"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Mark as "Canceled" status
                    </label>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  These settings determine whether projects with this status are considered completed or canceled.
                  Projects with these statuses will be filtered from the default dashboard view.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditForm(false);
                    setCurrentStatusType(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Status Types List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <h3 className="text-lg font-medium py-4 px-6 bg-gray-50 border-b border-gray-200">
          {entityTypes.find(et => et.id === activeEntityType)?.name} Status Types
        </h3>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="spinner"></div>
            <p className="mt-2 text-gray-500">Loading status types...</p>
          </div>
        ) : statusTypes.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No status types found for {entityTypes.find(et => et.id === activeEntityType)?.name.toLowerCase()}.
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="status-types">
              {(provided) => (
                <ul {...provided.droppableProps} ref={provided.innerRef} className="divide-y divide-gray-200">
                  {statusTypes.map((statusType, index) => (
                    <Draggable key={statusType.id} draggableId={statusType.id.toString()} index={index}>
                      {(provided) => (
                        <li
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="relative bg-white px-6 py-5 hover:bg-gray-50"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div 
                                className="h-8 w-8 rounded-full"
                                style={{ backgroundColor: statusType.color }}
                              ></div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900">
                                  {statusType.name}
                                </h4>
                                {statusType.description && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {statusType.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleEditClick(statusType)}
                                className="inline-flex items-center p-1.5 border border-gray-300 shadow-sm text-xs rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(statusType)}
                                disabled={statusType.is_default}
                                className={`inline-flex items-center p-1.5 border border-gray-300 shadow-sm text-xs rounded-md ${
                                  statusType.is_default
                                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                                    : 'text-red-700 bg-white hover:bg-red-50'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500`}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          {statusType.is_default && (
                            <span className="text-xs text-blue-600 font-medium mt-1 block">
                              Default status type
                            </span>
                          )}
                          {statusType.is_completed && (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium ml-2">
                              Completed
                            </span>
                          )}
                          {statusType.is_canceled && (
                            <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium ml-2">
                              Canceled
                            </span>
                          )}
                        </li>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>
    </div>
  );
};

export default StatusTypeSettings; 